/* eslint-disable max-len, @typescript-eslint/naming-convention */
import { StringService } from '../string';
import { LanguageService } from '../language';

// following types can be overidden by datasources
export interface DatasourceEnvironmentProps {
  adobeAnalytics: string;
  brightcoveAccountId: string;
  brightcovePlayerId: string;
  cludoCustomerId: string;
  cludoEngineId: string | string[];
  cognitoUserpoolClientId: string;
  cognitoUserpoolId: string;
  recaptchaKey: string;
  googleTagManagerId: string;
  livechatButtonId: string;
  livechatScriptId: string;
  livechatScriptUrl: string;
  munchkinAccountId: string;
  twitterHandle: string;
}

export interface GlobalConfigProps extends DatasourceEnvironmentProps {
  baseDomain: string;
  locale: string;
  pageId: string;
  translationUrl: string;
  whiteListedDomains: string;
  formsApi: string;
}

type HTMLElementContent = string | { toString: () => string };

export const DomService = {
  createElement(tagName: string, content: HTMLElementContent = '', props: Record<string, string> = {}): HTMLElement {
    const element = document.createElement(tagName);
    element.innerHTML = typeof content === 'string' ? content : content.toString();
    Object.keys(props).forEach((prop) => {
      element.setAttribute(prop, props[prop]);
    });
    return element;
  },

  getGlobalConfig(pageId: string,
    locale: string,
    environment: DatasourceEnvironmentProps,
  ): GlobalConfigProps {
    const parsedLocale = (locale === 'default' || !locale) ? LanguageService.defaultLocale : locale;

    return {
      baseDomain: process.env.GATSBY_BASE_DOMAIN,
      locale,
      pageId: `storyblok:${process.env.GATSBY_STORYBLOK_SPACE_API_KEY_NAME}:${pageId}`,
      translationUrl: `${process.env.GATSBY_ROCHE_COMPONENTS_LIBRARY_URL}/roche-component-library/assets/translations/${parsedLocale}.json`,
      whiteListedDomains: process.env.GATSBY_WHITE_LISTED_DOMAINS,
      formsApi: process.env.GATSBY_FORMS_API_URL,

      adobeAnalytics: environment.adobeAnalytics || process.env.GATSBY_ADOBE_ANALYTICS,
      brightcoveAccountId: environment.brightcoveAccountId
        || process.env.GATSBY_BRIGHTCOVE_ACCOUNT_ID,
      brightcovePlayerId: environment.brightcovePlayerId || process.env.GATSBY_BRIGHTCOVE_PLAYER_ID,
      cludoCustomerId: environment.cludoCustomerId || process.env.GATSBY_CLUDO_CUSTOMER_ID,
      cludoEngineId: (environment.cludoEngineId as string || process.env.GATSBY_CLUDO_ENGINE_ID)
        .replace(/[\s]/g, '')
        .split(',')
        .filter((item) => item.indexOf(`${parsedLocale}:`) === 0)
        .join()
        .substring(parsedLocale.length + 1, 8),
      cognitoUserpoolClientId: environment.cognitoUserpoolClientId
        || process.env.GATSBY_COGNITO_USERPOOL_CLIENT_ID,
      cognitoUserpoolId: environment.cognitoUserpoolId || process.env.GATSBY_COGNITO_USERPOOL_ID,
      recaptchaKey: environment.recaptchaKey || process.env.GATSBY_GOOGLE_RECAPTCHA_KEY,
      googleTagManagerId: environment.googleTagManagerId
        || process.env.GATSBY_ROCHE_GOOGLE_TAG_MANAGER_ID,
      livechatButtonId: environment.livechatButtonId || process.env.GATSBY_LIVECHAT_BUTTON_ID,
      livechatScriptId: environment.livechatScriptId || process.env.GATSBY_LIVECHAT_SCRIPT_ID,
      livechatScriptUrl: environment.livechatScriptUrl || process.env.GATSBY_LIVECHAT_SCRIPT_URL,
      munchkinAccountId: environment.munchkinAccountId || process.env.GATSBY_MUNCHKIN_ACCOUNT_ID,
      twitterHandle: environment.twitterHandle || process.env.GATSBY_TWITTER_HANDLE,
    };
  },

  getDOMGlobalConfig(): Partial<GlobalConfigProps> {
    const $rocheGlobalConfig = document.querySelector('roche-global-config');
    const config = $rocheGlobalConfig?.getAttributeNames().reduce((acc, attribute) => {
      const name = StringService.kebabToCamel(attribute.replace('data-', ''));
      return { ...acc, [name]: $rocheGlobalConfig.getAttribute(attribute) };
    }, {});
    return config;
  },
};
